import { type VariantProps, cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import type * as React from "react";

import { cn } from "cn";
import styles from "./button.module.css";

/**
 * Rules for tinting a button:
 *
 *   <Button variant="outline" tint="destructive">Delete Network</Button>
 *
 *   <div data-tint="warning">
 *     <Button variant="outline">Place Sell Orders</Button>
 *   </div>
 *
 * How it works:
 *
 * - `tint` sets `data-tint` on the button. Any ancestor can set the same
 *   attribute instead, and every button inside it picks the tint up — that is
 *   how an alert band tints its own actions without touching their call sites.
 * - `data-tint` rebinds the `--btn-*` custom properties declared in
 *   app/globals.css; the variant classes read them. Nothing selects the button
 *   from outside, so a button's own tint beats an ancestor's by proximity, and
 *   a `className` at the call site still beats both.
 * - Only `outline` reads the vars. A tint on any other variant is a no-op — add
 *   the var reads to that variant, do not patch the call site.
 *
 * Which tint — choose by meaning, not by what the button sits on:
 *
 * - `destructive` for an action that deletes, revokes or leaves. Text only: the
 *   surface stays neutral, because a filled red button is the page's one primary
 *   action and that is `variant="destructive"`.
 * - `warning` for an action tied to a consequence the user should acknowledge,
 *   such as requesting a higher node-hour limit or adding credits to deploy. A
 *   band that carries that meaning can set it once for every button inside.
 *   Its text takes the hue; the surface stays neutral.
 * - Those two. A hue you need once is a hue you do not need.
 *
 * Contrast:
 *
 * - A tint never repaints the surface, at rest or on hover. A button tinted to
 *   match the band it sits on disappears into it, leaving only its edge to read
 *   as a control — white against the band is what makes it look raised.
 *
 * Not a tint:
 *
 * - Invalid form state is `aria-invalid`, already in the base classes. Do not
 *   reach for a tint to mark a field-level error.
 * - Do not write color utilities in a Button's `className`. A color worth having
 *   is worth a tint; a color not worth a tint is noise.
 *
 * The ticket decides what a button says. These rules decide what color it is.
 */
const buttonVariants = cva(
  "inline-flex cursor-default items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-white dark:ring-offset-slate-950 transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation aria-invalid:border-red-500 aria-invalid:focus-visible:ring-red-500/40 aria-invalid:focus-visible:border-red-500",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        text: "px-0! py-0! h-fit! text-neutral-800! hover:text-neutral-500! dark:text-neutral-200! dark:hover:text-neutral-400! rounded-sm!",
        destructive: "bg-red-500 text-neutral-50 hover:bg-red-500/90",
        outline:
          "shadow-md bg-(--btn-surface) text-(color:--btn-fg) hover:bg-(--btn-surface-hover) hover:text-(color:--btn-fg-hover) active:bg-(--btn-surface-active) active:border-neutral-300",
        inset:
          "border bg-muted shadow-[inset_0_0_0px_1px_oklch(1_0_0_/_80%)] hover:bg-neutral-100 hover:text-neutral-900 dark:shadow-[inset_0_0_0px_1px_oklch(1_0_0_/_15%)] dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
        primary: `${styles.primary} focus-visible:ring-offset-primary-900 focus-visible:outline focus-visible:ring-2 focus-visible:ring-solid focus-visible:outline-solid focus-visible:outline-offset-4 focus-visible:outline-2 focus-visible:outline-primary-900`,
        secondary: "bg-neutral-100 text-neutral-900 hover:bg-neutral-100/80 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-800/80",
        ghost: "hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100",
        link: "text-neutral-900 hover:underline p-0! h-fit! dark:text-neutral-100",
        baremetal: "",
      },
      size: {
        default: "h-10 px-4 py-2 tracking-wide font-medium",
        xs: "h-5 px-2 text-xs",
        sm: "h-7 px-3 text-xs font-medium tracking-wide",
        md: "h-9 px-4 text-xs font-medium tracking-wide",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
      shape: {
        default: "rounded-md",
        rounded: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "default",
      shape: "default",
    },
  },
);

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    tint?: "warning" | "destructive";
  };

const Button = ({
  className,
  variant,
  size,
  asChild = false,
  shape,
  tint,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      data-size={size}
      data-variant={variant}
      data-tint={tint}
      className={cn(
        buttonVariants({ variant, size, shape, className }),
        variant === "primary" && styles.after,
      )}
      {...props}
    />
  );
};
Button.displayName = "Button";

export { Button, buttonVariants };
